import { ChangeDetectionStrategy, Component, DestroyRef, effect, inject, input, output, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { DatePipe } from '@angular/common';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { Subscription, timer } from 'rxjs';

import { FeedFileSetService } from '../services/feed-file-set.service';
import { FeedFileSet, FeedFileSetEntry, FeedFileSetStatus } from '../models/provider.model';
import { FeedFileListComponent } from '../shared/feed-file-list.component';
import { ToastService } from '../../../shared/services/toast-service';
import { LabelBadge, LabelBadgeDot } from '../../../shared/components/label-badge/label-badge';
import { Icon } from '../../../shared/components/icon/icon';
import { formatBytes } from '../../../shared/utils/format-bytes';
import { relativeTime } from '../../../shared/utils/relative-time';
import { apiErrorMessage, openDownloadTab } from '../util/feed-file-upload';

/** Re-list while any set is still waiting on GTFS's verdict. */
const PENDING_POLL_MS = 10_000;

const STATUS_DOT: Record<FeedFileSetStatus, LabelBadgeDot> = {
  PENDING: 'yellow',
  IMPORTED: 'green',
  FAILED: 'red',
};

/**
 * Static schedule tab of the GTFS feed page. Top: the feed's GTFS zip as a
 * feed file (`FeedFileListComponent` limited to one `gtfs.zip`, `.zip` only) —
 * uploaded manually or watched at a URL, since GTFS imports the only file of
 * a set (or else its single zip). Below: the file sets core built from it
 * (newest 5) as a table — status, file with a "LIVE · vN" mark on the set GTFS
 * is serving, build time, size, download (multi-file sets expand to per-file
 * links). Polls while any set is PENDING and emits `changed` after a file
 * change or once a pending set resolves, so the page can refresh health and
 * the feed's files summary.
 */
@Component({
  selector: 'app-gtfs-static-schedule',
  standalone: true,
  imports: [TranslocoDirective, DatePipe, LabelBadge, Icon, FeedFileListComponent],
  templateUrl: './gtfs-static-schedule.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block' },
})
export class GtfsStaticScheduleComponent {
  private readonly fileSetService = inject(FeedFileSetService);
  private readonly toastService = inject(ToastService);
  private readonly transloco = inject(TranslocoService);
  private readonly destroyRef = inject(DestroyRef);

  readonly feedId = input.required<string>();
  /** File set the live static version came from (diagnostics `static.fileSetId`). */
  readonly liveFileSetId = input<string | null>(null);
  readonly liveVersion = input<number | null>(null);
  readonly changed = output<void>();

  readonly fileSets = signal<FeedFileSet[]>([]);
  readonly isLoading = signal(true);
  /** Multi-file sets whose file list is expanded. */
  readonly expanded = signal<ReadonlySet<string>>(new Set());
  readonly downloadingFileId = signal<string | null>(null);

  readonly formatBytes = formatBytes;
  readonly relativeTime = relativeTime;

  private pollSub: Subscription | null = null;
  private hadPending = false;

  private readonly loadOnFeed = effect(() => {
    this.feedId();
    untracked(() => this.fetch());
  });

  constructor() {
    this.destroyRef.onDestroy(() => this.pollSub?.unsubscribe());
  }

  statusDot(status: FeedFileSetStatus): LabelBadgeDot {
    return STATUS_DOT[status];
  }

  setBytes(set: FeedFileSet): number {
    return set.files.reduce((n, f) => n + f.size, 0);
  }

  /** Tooltip listing every file of a multi-file set. */
  fileList(set: FeedFileSet): string {
    return set.files.map((f) => `${f.filename} (${formatBytes(f.size)})`).join('\n');
  }

  isExpanded(set: FeedFileSet): boolean {
    return this.expanded().has(set.id);
  }

  toggleExpanded(set: FeedFileSet): void {
    this.expanded.update((current) => {
      const next = new Set(current);
      if (next.has(set.id)) next.delete(set.id);
      else next.add(set.id);
      return next;
    });
  }

  /** The zip changed (upload, URL source, delete) — a new set may have been built. */
  onFilesChanged(): void {
    this.fetch();
    this.changed.emit();
  }

  /** One file downloads directly; a multi-file set expands to per-file links. */
  onDownloadClick(set: FeedFileSet): void {
    if (set.files.length === 1) this.download(set, set.files[0]);
    else this.toggleExpanded(set);
  }

  download(set: FeedFileSet, file: FeedFileSetEntry): void {
    const tab = openDownloadTab();
    this.downloadingFileId.set(file.id);
    this.fileSetService
      .getDownloadUrl(this.feedId(), set.id, file.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.downloadingFileId.set(null);
          tab.go(res.data.url);
        },
        error: (err: HttpErrorResponse) => {
          this.downloadingFileId.set(null);
          tab.cancel();
          this.toastService.show(apiErrorMessage(err) ?? this.transloco.translate<string>('providers.feedFiles.toast.downloadFailed'), 'error');
        },
      });
  }

  private fetch(): void {
    this.pollSub?.unsubscribe();
    this.pollSub = null;
    this.fileSetService
      .getAll(this.feedId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.fileSets.set(res.data ?? []);
          this.isLoading.set(false);
          const pending = this.fileSets().some((s) => s.status === 'PENDING');
          // A pending set just resolved: the live version (and health) may have changed.
          if (this.hadPending && !pending) this.changed.emit();
          this.hadPending = pending;
          if (pending) this.pollSub = timer(PENDING_POLL_MS).subscribe(() => this.fetch());
        },
        error: (err: HttpErrorResponse) => {
          this.isLoading.set(false);
          this.toastService.show(apiErrorMessage(err) ?? this.transloco.translate<string>('providers.feedFiles.toast.loadFailed'), 'error');
        },
      });
  }
}
